import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Select from "react-select";
import { getSources, saveSource } from "../../components/Api/GmsSource";
import { getGenders } from "../../components/Api/GmsGender";
import { getCountries } from "../../components/Api/GmsCountry";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { SquarePen, Plus, Trash2, Eye, User, Globe, MessageCircle } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import classnames from "classnames";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Social Media Platforms
const socialMediaPlatforms = [
  { value: "YTB", label: "YouTube" },
  { value: "INS", label: "Instagram" },
  { value: "SNP", label: "Snapchat" },
  { value: "FBK", label: "Facebook" },
  { value: "TKT", label: "TikTok" },
  { value: "XTR", label: "X (Twitter)" },
  { value: "WAP", label: "WhatsApp" },
  { value: "OTH", label: "Other" },
];

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  const businessId = normalizeId(authUser.companyId || user.user_Company_Id);
  const branchId = normalizeId(authUser.branchId || user.user_Branch_Id);

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
  };
};

const defaultForm = {
  Source_Id: 0,
  SourceName: "",
  SourceDetails: "",
  IsInfluencer: false,
  Nickname: "",
  GenderId: null,
  CountryId: null,
  BusinessId: null,
  BranchId: null,
  SocialMediaLinks: [],
  CreatedBy: 1,
  CreatedOn: new Date().toISOString(),
  ModifiedBy: null,
  ModifiedOn: null,
};

export default function SourcePage() {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [viewModal, setViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Dropdown data
  const [genders, setGenders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSource');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSource');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSource');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadSources();
      fetchDropdownData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  useEffect(() => {
    if (page > 1) {
      loadSources();
    }
  }, [page]);

  // Load branches when business changes
  useEffect(() => {
    if (form.BusinessId) {
      loadBranchesForBusiness(form.BusinessId);
    } else {
      setBranches([]);
    }
  }, [form.BusinessId]);

  const fetchDropdownData = async () => {
    try {
      const [gData, cData, bData] = await Promise.all([
        getGenders(),
        getCountries(),
        getBusinesses({ business_Active: true }),
      ]);

      const gendersData = Array.isArray(gData) ? gData : (gData?.Genders || gData?.genders || gData?.data || []);
      const countriesData = Array.isArray(cData) ? cData : (cData?.Countries || cData?.countries || cData?.data || []);
      const businessesData = Array.isArray(bData) ? bData : (bData?.Businesses || bData?.businesses || bData?.data || []);

      setGenders(gendersData);
      setCountries(countriesData);
      setBusinesses(businessesData);

      // If session has businessId, set it and load branches
      if (session.businessId && !form.BusinessId && !isEditing) {
        setForm(prev => ({ ...prev, BusinessId: session.businessId }));
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const loadBranchesForBusiness = async (businessId) => {
    try {
      const response = await getBranches({ Branch_Buisness_Id: businessId, branch_Active: true });
      const branchesData = Array.isArray(response) ? response : (response?.Branches || response?.branches || response?.data || []);
      // Use branch_Buisness_Id (as per API response) to filter
      const filteredBranches = branchesData.filter(branch => 
        (branch.Branch_Buisness_Id || branch.branch_Buisness_Id) === businessId
      );
      setBranches(filteredBranches);
      
      // If session has branchId and it belongs to this business, set it
      if (session.branchId && !form.BranchId && !isEditing) {
        const branchInList = filteredBranches.find(b => (b.branch_Id || b.Branch_Id) === session.branchId);
        if (branchInList) {
          setForm(prev => ({ ...prev, BranchId: session.branchId }));
        }
      }
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadSources = async () => {
    setLoading(true);
    try {
      const filter = {
        Page: page,
        PageSize: pageSize,
      };
      const response = await getSources(filter);
      
      const sourcesData = Array.isArray(response) ? response : (response?.Sources || response?.sources || response?.data || []);
      const count = response?.TotalCount || response?.totalCount || (Array.isArray(response) ? response.length : sourcesData.length);
      
      setSources(sourcesData);
      setTotalCount(count);
    } catch (error) {
      console.error("Error loading sources:", error);
      Swal.fire("Error", "Failed to load sources: " + (error.response?.data?.message || error.message), "error");
      setSources([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddSocialMedia = () => {
    setForm(prev => ({
      ...prev,
      SocialMediaLinks: [
        ...prev.SocialMediaLinks,
        { SocialMediaType: "YTB", SocialMediaLink: "" }
      ]
    }));
  };

  const handleRemoveSocialMedia = (index) => {
    setForm(prev => ({
      ...prev,
      SocialMediaLinks: prev.SocialMediaLinks.filter((_, i) => i !== index)
    }));
  };

  const handleSocialMediaChange = (index, field, value) => {
    const updatedLinks = [...form.SocialMediaLinks];
    updatedLinks[index][field] = value;
    setForm(prev => ({ ...prev, SocialMediaLinks: updatedLinks }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.BusinessId) {
      Swal.fire("Error", "Please select Company", "error");
      return;
    }
    if (!form.BranchId) {
      Swal.fire("Error", "Please select Branch", "error");
      return;
    }
    if (!form.SourceName || !form.SourceName.trim()) {
      Swal.fire("Error", "Please enter source name", "error");
      return;
    }

    if (form.IsInfluencer) {
        if (!form.Nickname || !form.Nickname.trim()) {
            Swal.fire("Error", "Please enter nickname for influencer", "error");
            return;
        }
        // Check if any social media links are incomplete
        const incompleteLink = form.SocialMediaLinks.find(l => !l.SocialMediaLink.trim());
        if (incompleteLink) {
            Swal.fire("Error", "Please enter all social media links", "error");
            return;
        }
    }
    
    setSaveLoading(true);
    try {
      const formData = {
        ...form,
        CreatedBy: session.userId,
        ModifiedBy: isEditing ? session.userId : null,
      };
      
      const result = await saveSource(formData);
      
      if (result.id && result.id > 0) {
        Swal.fire("Success", result.message || "Source saved successfully", "success");
        setForm({
            ...defaultForm,
            BusinessId: session.businessId,
            BranchId: session.branchId
        });
        setIsEditing(false);
        loadSources();
        setActiveTab("2");
      } else {
        Swal.fire("Error", result.message || "Failed to save source", "error");
      }
    } catch (error) {
      console.error("Error saving source:", error);
      Swal.fire("Error", "Failed to save source: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (source) => {
    // Map social media links from camelCase to PascalCase if needed
    const rawLinks = source.SocialMediaLinks || source.socialMediaLinks || [];
    const normalizedLinks = rawLinks.map(link => ({
        SourceSocialMediaId: link.SourceSocialMediaId || link.sourceSocialMediaId || 0,
        SourceId: link.SourceId || link.sourceId || 0,
        SocialMediaType: link.SocialMediaType || link.socialMediaType || "YTB",
        SocialMediaLink: link.SocialMediaLink || link.socialMediaLink || "",
    }));

    setForm({
      Source_Id: source.Source_Id || source.source_Id || 0,
      SourceName: source.SourceName || source.sourceName || "",
      SourceDetails: source.SourceDetails || source.sourceDetails || "",
      IsInfluencer: source.IsInfluencer || source.isInfluencer || false,
      Nickname: source.Nickname || source.nickname || "",
      GenderId: source.GenderId || source.genderId || null,
      CountryId: source.CountryId || source.countryId || null,
      BusinessId: source.BusinessId || source.businessId || null,
      BranchId: source.BranchId || source.branchId || null,
      SocialMediaLinks: normalizedLinks,
      CreatedBy: source.CreatedBy || source.createdBy || session.userId,
      CreatedOn: source.CreatedOn || source.createdOn || new Date().toISOString(),
      ModifiedBy: session.userId,
      ModifiedOn: new Date().toISOString(),
    });
    setIsEditing(true);
    setActiveTab("1");
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm({
        ...defaultForm,
        BusinessId: session.businessId,
        BranchId: session.branchId
    });
    setIsEditing(false);
  };

  const handleView = (source) => {
    // Normalize links for viewing as well
    const rawLinks = source.SocialMediaLinks || source.socialMediaLinks || [];
    const normalizedLinks = rawLinks.map(link => ({
        SocialMediaType: link.SocialMediaType || link.socialMediaType || "OTH",
        SocialMediaLink: link.SocialMediaLink || link.socialMediaLink || "",
    }));

    setViewData({
        ...source,
        SocialMediaLinks: normalizedLinks
    });
    setViewModal(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (!hasPermission) {
    return null;
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Source" breadcrumbItem="GMS" />
          
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="wizard clearfix">
                    <div className="steps clearfix">
                      <ul>
                        {!isViewOnly && (
                          <NavItem className={classnames({ current: activeTab === "1" })}>
                            <NavLink
                              className={classnames({ current: activeTab === "1" })}
                              onClick={() => setActiveTab("1")}
                            >
                              <span className="number">1.</span> {isEditing ? "Edit Source" : "Add Source"}
                            </NavLink>
                          </NavItem>
                        )}
                        <NavItem className={classnames({ current: activeTab === "2" })}>
                          <NavLink
                            className={classnames({ active: activeTab === "2" })}
                            onClick={() => setActiveTab("2")}
                          >
                            <span className="number">2.</span> Source List
                          </NavLink>
                        </NavItem>
                      </ul>
                    </div>

                    <div className="content clearfix">
                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                          {!isViewOnly && (
                            <div>
                              <Card>
                                <CardBody>
                                  <h5 className="card-title mb-4">
                                    {isEditing ? "Edit Source" : "Add New Source"}
                                  </h5>
                                  <Form onSubmit={handleSubmit}>
                                    <Row>
                                      <Col md={6}>
                                        <FormGroup className="mb-3">
                                          <Label>Company <span className="text-danger">*</span></Label>
                                          <Select
                                            options={businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn }))}
                                            value={businesses.filter(b => b.business_Id === form.BusinessId).map(b => ({ value: b.business_Id, label: b.business_NameEn }))[0] || null}
                                            onChange={(opt) => handleFormChange("BusinessId", opt?.value || null)}
                                            styles={selectStyles}
                                            placeholder="Select Company"
                                            isClearable
                                            isDisabled={!!session.businessId}
                                          />
                                        </FormGroup>
                                      </Col>
                                      <Col md={6}>
                                        <FormGroup className="mb-3">
                                          <Label>Branch <span className="text-danger">*</span></Label>
                                          <Select
                                            options={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))}
                                            value={branches.filter(b => b.branch_Id === form.BranchId).map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))[0] || null}
                                            onChange={(opt) => handleFormChange("BranchId", opt?.value || null)}
                                            styles={selectStyles}
                                            placeholder="Select Branch"
                                            isClearable
                                            isDisabled={!!session.branchId}
                                          />
                                        </FormGroup>
                                      </Col>
                                    </Row>

                                    <Row>
                                      <Col md={6}>
                                        <FormGroup className="mb-3">
                                          <Label>Source Name <span className="text-danger">*</span></Label>
                                          <Input
                                            type="text"
                                            name="SourceName"
                                            value={form.SourceName}
                                            onChange={(e) => handleFormChange("SourceName", e.target.value)}
                                            placeholder="Enter source name"
                                            required
                                          />
                                        </FormGroup>
                                      </Col>
                                      <Col md={6}>
                                        <FormGroup className="mb-3">
                                          <Label>Source Details</Label>
                                          <Input
                                            type="text"
                                            name="SourceDetails"
                                            value={form.SourceDetails}
                                            onChange={(e) => handleFormChange("SourceDetails", e.target.value)}
                                            placeholder="Enter source details"
                                          />
                                        </FormGroup>
                                      </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup className="mb-4">
                                                <div className="form-check form-switch form-switch-lg mb-3">
                                                    <Input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="isInfluencer"
                                                        checked={form.IsInfluencer}
                                                        onChange={(e) => handleFormChange("IsInfluencer", e.target.checked)}
                                                    />
                                                    <Label className="form-check-label" htmlFor="isInfluencer">
                                                        <strong>Is Influencer?</strong>
                                                    </Label>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {form.IsInfluencer && (
                                        <div className="p-3 mb-4 bg-light rounded shadow-sm border">
                                            <h6 className="mb-3 text-primary border-bottom pb-2">Influencer Details</h6>
                                            <Row>
                                                <Col md={4}>
                                                    <FormGroup className="mb-3">
                                                        <Label>Nickname <span className="text-danger">*</span></Label>
                                                        <Input
                                                            type="text"
                                                            value={form.Nickname}
                                                            onChange={(e) => handleFormChange("Nickname", e.target.value)}
                                                            placeholder="Enter nickname"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup className="mb-3">
                                                        <Label>Gender</Label>
                                                        <Select
                                                            options={genders.map(g => ({ value: g.gender_Id, label: g.gender_NameEn }))}
                                                            value={genders.filter(g => g.gender_Id === form.GenderId).map(g => ({ value: g.gender_Id, label: g.gender_NameEn }))[0] || null}
                                                            onChange={(opt) => handleFormChange("GenderId", opt?.value || null)}
                                                            styles={selectStyles}
                                                            placeholder="Select Gender"
                                                            isClearable
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup className="mb-3">
                                                        <Label>Nationality</Label>
                                                        <Select
                                                            options={countries.map(c => ({ value: c.country_Id, label: c.country_NameEn }))}
                                                            value={countries.filter(c => c.country_Id === form.CountryId).map(c => ({ value: c.country_Id, label: c.country_NameEn }))[0] || null}
                                                            onChange={(opt) => handleFormChange("CountryId", opt?.value || null)}
                                                            styles={selectStyles}
                                                            placeholder="Select Nationality"
                                                            isClearable
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>


                                            <div className="mt-4">
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <h6 className="text-secondary m-0">Social Media Links</h6>
                                                    <Button color="success" size="sm" onClick={handleAddSocialMedia} className="d-flex align-items-center">
                                                        <Plus size={16} className="me-1" /> Add Link
                                                    </Button>
                                                </div>
                                                {form.SocialMediaLinks.length === 0 && (
                                                    <div className="text-center py-3 text-muted border rounded bg-white" style={{ borderStyle: 'dashed' }}>
                                                        No social media links added yet.
                                                    </div>
                                                )}
                                                {form.SocialMediaLinks.map((link, index) => (
                                                    <Row key={index} className="mb-2 align-items-end g-2">
                                                        <Col md={4}>
                                                            <Input
                                                                type="select"
                                                                value={link.SocialMediaType}
                                                                onChange={(e) => handleSocialMediaChange(index, "SocialMediaType", e.target.value)}
                                                            >
                                                                {socialMediaPlatforms.map(p => (
                                                                    <option key={p.value} value={p.value}>{p.label}</option>
                                                                ))}
                                                            </Input>
                                                        </Col>
                                                        <Col md={7}>
                                                            <Input
                                                                type="text"
                                                                value={link.SocialMediaLink}
                                                                onChange={(e) => handleSocialMediaChange(index, "SocialMediaLink", e.target.value)}
                                                                placeholder="Enter profile link or handle"
                                                            />
                                                        </Col>
                                                        <Col md={1}>
                                                            <Button color="danger" size="sm" onClick={() => handleRemoveSocialMedia(index)} className="w-100">
                                                                <Trash2 size={16} />
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-flex gap-2">
                                      <Button
                                        type="submit"
                                        color="primary"
                                        disabled={saveLoading}
                                      >
                                        {saveLoading ? (
                                          <>
                                            <ClipLoader size={16} color="#ffffff" className="me-2" />
                                            Saving...
                                          </>
                                        ) : isEditing ? (
                                          "Update Source"
                                        ) : (
                                          "Save Source"
                                        )}
                                      </Button>
                                      {isEditing && (
                                        <Button
                                          type="button"
                                          color="secondary"
                                          onClick={handleCancelEdit}
                                        >
                                          Cancel
                                        </Button>
                                      )}
                                    </div>
                                  </Form>
                                </CardBody>
                              </Card>
                            </div>
                          )}
                        </TabPane>

                        <TabPane tabId="2">
                          <div>
                            <Card>
                              <CardBody>
                                <h5 className="card-title mb-3">
                                  Source List ({totalCount} records)
                                </h5>
                                
                                {loading ? (
                                  <div className="text-center py-4">
                                    <ClipLoader color="#556ee6" loading={loading} size={50} />
                                  </div>
                                ) : (
                                  <>
                                    <div className="table-responsive">
                                      <Table bordered className="align-middle">
                                        <thead className="table-light">
                                          <tr>
                                            <th>Sr.No</th>
                                            <th>Source Name</th>
                                            <th>Details</th>
                                            <th className="text-center">Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sources.length === 0 ? (
                                            <tr>
                                              <td colSpan={4} className="text-center text-muted">
                                                No sources found
                                              </td>
                                            </tr>
                                          ) : (
                                            sources.map((source, index) => {
                                              const sourceId = source.Source_Id || source.source_Id;
                                              const sourceName = source.SourceName || source.sourceName || 'N/A';
                                              const isInfluencer = source.IsInfluencer || source.isInfluencer;
                                              const nickname = source.Nickname || source.nickname;
                                              const sourceDetails = source.SourceDetails || source.sourceDetails || '';
                                              
                                              return (
                                                <tr key={sourceId}>
                                                  <td>{(page - 1) * pageSize + index + 1}</td>
                                                  <td>
                                                      <div className="fw-bold text-primary">{sourceName}</div>
                                                      {isInfluencer && <small className="text-muted">@{nickname}</small>}
                                                  </td>
                                                  <td>
                                                      {sourceDetails || "-"}
                                                  </td>
                                                  <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-2">
                                                        {isInfluencer && (
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                onClick={() => handleView(source)}
                                                                title="View Details"
                                                                className="btn-soft-primary"
                                                            >
                                                                <Eye size={14} className="me-1" />
                                                                View
                                                            </Button>
                                                        )}
                                                        {hasEditOrHigher && (
                                                            <Button
                                                                color="info"
                                                                size="sm"
                                                                onClick={() => handleEdit(source)}
                                                                title="Edit Source"
                                                                className="btn-soft-info"
                                                            >
                                                                <SquarePen size={14} className="me-1" />
                                                                Edit
                                                            </Button>
                                                        )}
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })
                                          )}
                                        </tbody>
                                      </Table>
                                    </div>

                                    {/* Pagination */}
                                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                                      </div>
                                      {totalCount > pageSize && (
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                          <Button
                                            color="light"
                                            size="sm"
                                            disabled={page === 1}
                                            onClick={() => handlePageChange(page - 1)}
                                            style={{
                                              border: '1px solid #dee2e6',
                                              borderRadius: '0.375rem',
                                              minWidth: '32px',
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                              color: page === 1 ? '#6c757d' : '#495057'
                                            }}
                                          >
                                            <i className="mdi mdi-chevron-left"></i>
                                          </Button>
                                          
                                          {[...Array(totalPages)].map((_, index) => {
                                            const pageNum = index + 1;
                                            const isActive = page === pageNum;
                                            
                                            return (
                                              <Button
                                                key={pageNum}
                                                color={isActive ? "primary" : "light"}
                                                size="sm"
                                                onClick={() => handlePageChange(pageNum)}
                                                style={{
                                                  border: '1px solid #dee2e6',
                                                  borderRadius: '0.375rem',
                                                  minWidth: '32px',
                                                  height: '32px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                                  color: isActive ? '#fff' : '#495057',
                                                  fontWeight: isActive ? '600' : '400',
                                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                                }}
                                              >
                                                {pageNum}
                                              </Button>
                                            );
                                          })}
                                          
                                          <Button
                                            color="light"
                                            size="sm"
                                            disabled={page === totalPages}
                                            onClick={() => handlePageChange(page + 1)}
                                            style={{
                                              border: '1px solid #dee2e6',
                                              borderRadius: '0.375rem',
                                              minWidth: '32px',
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              backgroundColor: page === totalPages ? '#f8f9fa' : '#fff',
                                              color: page === totalPages ? '#6c757d' : '#495057'
                                            }}
                                          >
                                            <i className="mdi mdi-chevron-right"></i>
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </CardBody>
                            </Card>
                          </div>
                        </TabPane>
                      </TabContent>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Influencer Details Modal */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} centered size="md">
        <ModalHeader toggle={() => setViewModal(false)} className="bg-primary text-white">
          <div className="d-flex align-items-center">
            <User size={20} className="me-2" />
            <span>Influencer Profile</span>
          </div>
        </ModalHeader>
        <ModalBody className="p-4">
          {viewData && (
            <div>
                <div className="text-center mb-4">
                    <div className="avatar-md mx-auto mb-3">
                        <span className="avatar-title rounded-circle bg-soft-primary text-primary display-6">
                            {(viewData.SourceName || viewData.sourceName || "?").charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <h4 className="mb-1 text-primary">{viewData.SourceName || viewData.sourceName}</h4>
                    <p className="text-muted font-size-14">@{viewData.Nickname || viewData.nickname}</p>
                </div>

                <Row className="mb-4 text-center g-3">
                    <Col xs={6}>
                        <div className="p-3 border rounded bg-light shadow-sm h-100">
                            <User size={18} className="text-primary mb-2" />
                            <p className="text-muted mb-1 font-size-12">Gender</p>
                            <h6 className="mb-0">
                                {genders.find(g => g.gender_Id === (viewData.GenderId || viewData.genderId))?.gender_NameEn || "N/A"}
                            </h6>
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div className="p-3 border rounded bg-light shadow-sm h-100">
                            <Globe size={18} className="text-primary mb-2" />
                            <p className="text-muted mb-1 font-size-12">Nationality</p>
                            <h6 className="mb-0">
                                {countries.find(c => c.country_Id === (viewData.CountryId || viewData.countryId))?.country_NameEn || "N/A"}
                            </h6>
                        </div>
                    </Col>
                </Row>

                <h6 className="text-primary mb-3 d-flex align-items-center">
                    <MessageCircle size={18} className="me-2" />
                    Social Media Presence
                </h6>
                
                <div className="list-group list-group-flush border rounded overflow-hidden">
                    {(viewData.SocialMediaLinks || viewData.socialMediaLinks || []).length > 0 ? (
                        (viewData.SocialMediaLinks || viewData.socialMediaLinks).map((link, i) => {
                            const platform = socialMediaPlatforms.find(p => p.value === (link.SocialMediaType || link.socialMediaType));
                            
                            // Map icon classes for standard platforms
                            const iconMap = {
                                YTB: "mdi mdi-youtube text-danger",
                                INS: "mdi mdi-instagram text-pink",
                                SNP: "mdi mdi-snapchat text-warning",
                                FBK: "mdi mdi-facebook text-primary",
                                TKT: "mdi mdi-music-note text-dark",
                                XTR: "mdi mdi-twitter text-info",
                                WAP: "mdi mdi-whatsapp text-success",
                                OTH: "mdi mdi-link text-secondary"
                            };

                            const iconClass = iconMap[link.SocialMediaType || link.socialMediaType] || "mdi mdi-link";
                            
                            // Helper to ensure URL is absolute
                            const linkUrl = link.SocialMediaLink || link.socialMediaLink;
                            const formattedUrl = linkUrl && !/^https?:\/\//i.test(linkUrl) 
                                ? `https://${linkUrl}` 
                                : linkUrl;

                            return (
                                <div key={i} className="list-group-item d-flex align-items-center py-3">
                                    <div className="flex-shrink-0 me-3">
                                        <div className="avatar-xs">
                                            <span className="avatar-title rounded-circle bg-light border">
                                                <i className={`${iconClass} font-size-18`}></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden">
                                        <h6 className="font-size-14 mb-1 text-truncate">{platform?.label || "Link"}</h6>
                                        <a href={formattedUrl} target="_blank" rel="noopener noreferrer" className="text-muted text-truncate font-size-13 d-block">
                                            {linkUrl}
                                        </a>
                                    </div>
                                    <div className="flex-shrink-0 ms-2">
                                        <a href={formattedUrl} target="_blank" rel="noopener noreferrer">
                                            <i className="mdi mdi-open-in-new text-muted"></i>
                                        </a>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="p-4 text-center text-muted italic">
                            No social media links provided.
                        </div>
                    )}
                </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}

